// Hãng tàu: mã 3 ký tự (CarrierCode) ↔ tên LOGI (CARRIER, dùng trong RouteCode) ↔ tên hiển thị.
// Nguồn: LOGI-data-dictionary.md (job-booking §2 "Cặp mã / tên hãng", TblCarrierAlias; crm-rates-svc §2.3, §3.3)
// và RouteCode trong board Mint-P2P / Mint-Rates. Hãng chưa rõ tên LOGI thì để trống `logiName`.
import { compactKey, matchKey } from './text';

export interface Carrier {
  /** CarrierCode 3 ký tự — dùng cho chip. */
  code: string;
  /** Giá trị cột CARRIER của LOGI, là đuôi của RouteCode (`ONE-LINE`, `CMA-CGM`). */
  logiName?: string;
  /** Tên đầy đủ (hiện khi rê chuột). */
  name: string;
  /** Cách viết khác gặp trong dữ liệu (TblCarrierAlias). */
  aliases?: readonly string[];
}

export const CARRIERS: readonly Carrier[] = [
  { code: 'ONE', logiName: 'ONE-LINE', name: 'Ocean Network Express', aliases: ['ONE_LINE', 'ONELINE', 'ONE - LINE'] },
  { code: 'COS', logiName: 'COSCO', name: 'COSCO Shipping' },
  { code: 'YML', logiName: 'YANGMING', name: 'Yang Ming', aliases: ['YANG MING'] },
  { code: 'MSC', logiName: 'MSC', name: 'MSC' },
  { code: 'ZIM', logiName: 'ZIM', name: 'ZIM' },
  { code: 'KMT', logiName: 'KMTC', name: 'KMTC' },
  { code: 'WHL', logiName: 'WANHAI', name: 'Wan Hai', aliases: ['WAN HAI'] },
  { code: 'SIT', logiName: 'SITC-NEW', name: 'SITC', aliases: ['SITC'] },
  { code: 'EMC', logiName: 'EVERGREEN', name: 'Evergreen' },
  { code: 'HLC', logiName: 'HAPAGLLOYD', name: 'Hapag-Lloyd', aliases: ['HAPAG-LLOYD', 'HLAG'] },
  { code: 'CMA', logiName: 'CMA-CGM', name: 'CMA CGM', aliases: ['CMA CGM'] },
  { code: 'PIL', logiName: 'PIL', name: 'Pacific International Lines' },
  { code: 'MSK', logiName: 'MAERSK', name: 'Maersk', aliases: ['MAERK'] },
  { code: 'NSS', logiName: 'NAMSUNG', name: 'Namsung' },
  { code: 'TSL', logiName: 'TSLINE', name: 'TS Lines', aliases: ['TS LINE'] },
  { code: 'HMM', logiName: 'HYUNDAI', name: 'HMM', aliases: ['HUYNDAI'] },
  { code: 'RCL', logiName: 'RCL', name: 'RCL' },
  { code: 'CUL', logiName: 'CULINE-HPH', name: 'CU Lines', aliases: ['CULINES'] },
  { code: 'IAL', logiName: 'INTERASIA', name: 'Interasia' },
  { code: 'SNL', logiName: 'SINOLINES', name: 'Sinolines', aliases: ['SINOTRAN -HPH'] },
  { code: 'HAL', logiName: 'HEUNGA', name: 'Heung-A', aliases: ['HEUNG A'] },
  { code: 'CNC', logiName: 'CNC', name: 'CNC' },
  { code: 'OOL', logiName: 'OOCL', name: 'OOCL' },
  { code: 'SKR', logiName: 'SINOKOR', name: 'Sinokor' },
  { code: 'NOS', logiName: 'NOS', name: 'NOS' },
  { code: 'POS', logiName: 'PANOCEAN', name: 'Pan Ocean', aliases: ['PAN OCEAN'] },
  { code: 'CKL', logiName: 'CKLINE', name: 'CK Line' },
  { code: 'DJS', logiName: 'DONGJIN', name: 'Dongjin Shipping' },
  { code: 'JJS', name: 'Jinjiang Shipping' },
  { code: 'ASL', name: 'Asean Seas Line' },
  { code: 'SML', name: 'SM Line' },
];

const INDEX = new Map<string, Carrier>();
for (const c of CARRIERS) {
  for (const k of [c.code, c.logiName, c.name, ...(c.aliases ?? [])]) {
    if (!k) continue;
    const key = compactKey(k);
    if (!INDEX.has(key)) INDEX.set(key, c);
  }
}

export function isCarrierCode(value: string): boolean {
  return /^[A-Z]{3}$/.test(value);
}

/** Tìm hãng theo mã / tên LOGI / tên / alias (không phân biệt hoa thường, dấu cách, gạch). */
export function findCarrier(value: string | null | undefined): Carrier | undefined {
  if (!value) return undefined;
  return INDEX.get(compactKey(value));
}

const SPOT = /[\s_-]*SPOT$/;

/**
 * Đọc tên hãng trong dữ liệu LOGI. Hậu tố `-Spot` (`MSC-Spot`, `COSCO -SPOT`, `Maersk-Spot`) = kênh giá spot.
 * Không nhận ra hãng thì `carrier` là `undefined`.
 */
export function resolveCarrier(value: string): { carrier: Carrier | undefined; spot: boolean } {
  const key = matchKey(value);
  const spot = SPOT.test(key) && key.replace(SPOT, '') !== '';
  return { carrier: findCarrier(spot ? key.replace(SPOT, '') : key), spot };
}
